import { z } from 'zod';
import { UserV4Schema } from './UserV4Schema'; // Impor skema UserV4 yang sudah kita definisikan

// ====================================================================
// Skema untuk Payload Request (POST /v4/user)
// ====================================================================

/**
 * Skema untuk objek filter dalam payload request.
 * Semua field bersifat opsional.
 */
const GetUsersFilterV4Schema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
});

/**
 * Skema Zod untuk keseluruhan payload yang dikirim saat mengambil daftar pengguna.
 * Ini memvalidasi objek sebelum dikirim ke API.
 */
export const GetUsersV4PayloadSchema = z.object({
    filter: GetUsersFilterV4Schema.optional(),
    /**
     * Pencarian bebas satu kolom input. Ini parameter TOP-LEVEL (`?search=`),
     * bukan bagian dari `filter[...]` milik Spatie QueryBuilder — backend
     * (UserController::index) menanganinya terpisah dan mencocokkannya ke
     * name, email, DAN phone sekaligus dengan OR.
     *
     * Sengaja tidak memakai `filter[name]` seperti sebelumnya, karena beberapa
     * `filter[...]` akan digabung dengan AND — mustahil dipakai untuk satu
     * kotak pencarian yang harus mencocokkan salah satu dari tiga kolom.
     */
    search: z.string().optional(),
    include: z.string().optional(),
    orderBy: z.string().optional(),
    sort: z.enum(['asc', 'desc']).optional(),
    limit: z.number().int().positive().optional(),
    page: z.number().int().positive().optional(), // 'page' ditambahkan untuk paginasi
    all: z.number().int().optional(),
});


// ====================================================================
// Skema untuk Respons Sukses (GET /v4/user)
// ====================================================================

/**
 * Skema untuk objek paginasi yang diterima dalam respons.
 * Didasarkan secara akurat pada contoh respons API.
 */
const PaginationV4Schema = z.object({
    current_page: z.number().int(),
    from: z.number().int().nullable(),
    to: z.number().int().nullable(),
    total: z.number().int(),
    per_page: z.number().int(),
    last_page: z.number().int(),
    next_page: z.number().int().nullable(),
    prev_page: z.number().int().nullable(),
    path: z.string().url(),
    next_page_url: z.string().url().nullable(),
    prev_page_url: z.string().url().nullable(),
    first_page_url: z.string().url(),
    last_page_url: z.string().url(),
});

/**
 * Skema Zod untuk keseluruhan respons sukses saat mengambil daftar pengguna.
 * Ini memvalidasi data yang diterima dari API.
 */
export const GetUsersV4ResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.array(UserV4Schema), // Menggunakan UserV4Schema untuk setiap item dalam array
    pagination: PaginationV4Schema,
});

/**
 * Skema untuk respons saat membuat pengguna baru.
 * Berdasarkan contoh respons POST yang Anda berikan.
 */
export const CreateUserV4ResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.object({
        id: z.string().uuid(),
        name: z.string(),
        phone: z.string(),
        email: z.string().email(),
        updated_at: z.string().datetime(),
        created_at: z.string().datetime(),
    }),
});

/**
 * Skema untuk respons saat mengambil atau memperbarui satu pengguna.
 * Berdasarkan contoh respons GET by ID dan PUT yang Anda berikan.
 */
export const GetUserV4ResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: UserV4Schema, // Menggunakan skema UserV4 yang sudah ada
});

// Alias untuk Update, karena strukturnya sama dengan Get by ID
export const UpdateUserV4ResponseSchema = GetUserV4ResponseSchema;


// ====================================================================
// Tipe TypeScript yang Diekspor
// ====================================================================

/**
 * Tipe TypeScript untuk payload request, diturunkan dari skema Zod.
 */
export type GetUsersV4Payload = z.infer<typeof GetUsersV4PayloadSchema>;

/**
 * Tipe TypeScript untuk objek paginasi, diturunkan dari skema Zod.
 */
export type PaginationV4 = z.infer<typeof PaginationV4Schema>;

/**
 * Tipe TypeScript untuk keseluruhan respons sukses, diturunkan dari skema Zod.
 */
export type GetUsersV4Response = z.infer<typeof GetUsersV4ResponseSchema>;

/**
 * Tipe TypeScript untuk respons pembuatan pengguna, diturunkan dari skema Zod.
 */
export type CreateUserV4Response = z.infer<typeof CreateUserV4ResponseSchema>;

/**
 * Tipe TypeScript untuk respons pengambilan satu pengguna, diturunkan dari skema Zod.
 */
export type GetUserV4Response = z.infer<typeof GetUserV4ResponseSchema>;

/**
 * Tipe TypeScript untuk respons pembaruan pengguna.
 */
export type UpdateUserV4Response = z.infer<typeof UpdateUserV4ResponseSchema>;